// The section model of the ☸ OPTIONS menu (ui-map-interface.md §13,
// R1731; card 20 of mockup round 081).
//
// One structure, two shells: the game menu and the hub SETTINGS panel show
// the same eight sections in the same order, with the same glyph, the same
// word and the same summary line. A desktop shows them as a rail beside
// the rows of one section; a telephone shows them as tiles, then one
// section a page. The commander row opens one more page, ACCOUNT, which is
// not a section: it stands in the session block and never in the rail.
//
// Everything here is pure. A host hands in the settings and the few facts
// the blob does not hold (the advisor switch of the empire, the standing
// orders of the account, the length of the block list), and reads words.

import type { ChartSetting, GameSettings } from '../settings';
import { LANGUAGES } from '../../gen/balance';
import { options as S } from '../../gen/strings';
import { textGlyph } from '../glyph';
import { percentOf } from './kit';
import { uiThemeLabel } from './themepick';

/** The eight sections, in the one order of R1731. SOCIAL took the place of
 *  CHAT (R1759). SEA VIEW (R2865) stands in every environment (R3111). */
export const SECTION_IDS = ['map', 'audio', 'iface', 'play', 'alerts', 'social', 'seaview', 'help'] as const;
export type SectionId = (typeof SECTION_IDS)[number];

/** A page of the menu: a section, or the ACCOUNT page the commander row
 *  opens. */
export type PageId = SectionId | 'account';

/** One section as every surface draws it. */
export interface Section {
  readonly id: PageId;
  /** The glyph, in its text form (`ui/glyph.ts`), so iOS never draws a
   *  colour emoji in its place. */
  readonly glyph: string;
  /** The word: the rail row, the tile and the page title. */
  readonly name: string;
  /** The one-line sentence under the page title. */
  readonly what: string;
}

/** The eight sections, in their order. */
export const SECTIONS: readonly Section[] = [
  { id: 'map', glyph: textGlyph('⬡'), name: S.sec_map, what: S.sec_map_what },
  { id: 'audio', glyph: textGlyph('♪'), name: S.sec_audio, what: S.sec_audio_what },
  { id: 'iface', glyph: textGlyph('▣'), name: S.sec_iface, what: S.sec_iface_what },
  { id: 'play', glyph: textGlyph('◈'), name: S.sec_play, what: S.sec_play_what },
  { id: 'alerts', glyph: textGlyph('◉'), name: S.sec_alerts, what: S.sec_alerts_what },
  { id: 'social', glyph: textGlyph('✉'), name: S.sec_social, what: S.sec_social_what },
  { id: 'seaview', glyph: textGlyph('∿'), name: S.sec_seaview, what: S.sec_seaview_what },
  { id: 'help', glyph: textGlyph('?'), name: S.sec_help, what: S.sec_help_what },
];

/** The ACCOUNT page. The commander row opens it; it is no section. */
export const ACCOUNT_PAGE: Section = {
  id: 'account', glyph: textGlyph('☸'), name: S.sec_account, what: S.sec_account_what,
};

/** True when `raw` names one of the eight sections. */
export function isSectionId(raw: unknown): raw is SectionId {
  return typeof raw === 'string' && (SECTION_IDS as readonly string[]).includes(raw);
}

/** True when `raw` names a page: a section or ACCOUNT. */
export function isPageId(raw: unknown): raw is PageId {
  return raw === 'account' || isSectionId(raw);
}

/** The section or page that `id` names. */
export function pageOf(id: PageId): Section {
  return id === 'account' ? ACCOUNT_PAGE : SECTIONS.find((s) => s.id === id) ?? SECTIONS[0];
}

/**
 * What a summary reads that the settings blob does not hold. A field a
 * host does not know is undefined, and the summary then leaves its part
 * out, or names the section's default line.
 */
export interface SummaryFacts {
  /** The host publishes sound. False draws "no sound yet". */
  sound?: boolean;
  /** A meta server and a session stand behind the page. False marks the
   *  section that needs one (ALERTS), and SOCIAL then names no block list. */
  account?: boolean;
  /** The advisor switch as the empire (in a world) or the account (in the
   *  hub) holds it (R1566). */
  advisor?: boolean;
  /** The standing-orders switch of the account (R1256). */
  standing?: boolean;
  /** How many accounts the block list holds, once read. */
  blocked?: number;
}

/** The word of a switch in a summary line. */
function onOff(on: boolean): string {
  return on ? S.sum_on : S.sum_off;
}

/** The summary word of a chart reading. */
const CHART_SUM: Record<ChartSetting, string> = {
  off: S.sum_chart_off,
  historical: S.sum_chart_historical,
  modern: S.sum_chart_modern,
};

/** A volume as a summary reads it: the level in percent, or OFF. */
function level(on: boolean, gain: number): string {
  return on ? String(percentOf(gain)) : S.sum_off;
}

/** The name of a language in that language, or the tag itself: the word
 *  the INTERFACE summary gives the language setting (R2207). */
export function endonym(tag: string): string {
  return LANGUAGES.find((l) => l.tag === tag)?.endonym ?? tag;
}

/** The SOCIAL summary of the two count switches (R1759), and the chat
 *  time stamp while it is on (R1791). */
function countsWord(s: GameSettings): string {
  const counts = s.chatCountOn && s.inboxCountOn ? S.sum_counts_on
    : !s.chatCountOn && !s.inboxCountOn ? S.sum_counts_off
      : s.chatCountOn ? S.sum_inbox_count_off : S.sum_chat_count_off;
  return s.chatTimeOn ? S.sum_chat_time_on({ counts }) : counts;
}

/** The summary line of one section (card 20): "chart modern · sonar on". */
export function summaryOf(id: SectionId, s: GameSettings, f: SummaryFacts = {}): string {
  switch (id) {
    case 'map':
      return S.sum_map({ chart: CHART_SUM[s.chart] ?? S.sum_chart_modern, sonar: onOff(s.sonar) });
    case 'audio':
      if (f.sound === false) return S.sum_audio_none;
      return S.sum_audio({ music: level(s.musicOn, s.musicVol), sfx: level(s.sfxOn, s.sfxVol) });
    case 'iface':
      // The theme label needs no entitlement set: a value out of the
      // list reads by its own id (`themepick.ts`).
      return S.sum_iface({ theme: uiThemeLabel(s, new Set()), scale: s.uiScale, lang: endonym(s.lang) });
    case 'play': {
      const advisor = f.advisor === undefined ? '—' : onOff(f.advisor);
      return f.standing === undefined
        ? S.sum_play({ advisor })
        : S.sum_play_standing({ advisor, standing: onOff(f.standing) });
    }
    case 'alerts':
      return f.account === false ? S.sum_no_account : S.sum_alerts;
    case 'social': {
      // The two count switches are keys of the blob, so they read without
      // an account; the block list needs one (R1759).
      const counts = countsWord(s);
      if (f.account === false || f.blocked === undefined) return counts;
      return S.sum_social({ counts, blocked: S.sum_blocked({ count: f.blocked }) });
    }
    case 'seaview':
      return s.seaIdle ? S.sum_seaview_on({ min: s.seaIdleMin }) : S.sum_seaview_off;
    case 'help':
      return S.sum_help;
  }
}

/** Every summary at once, in section order. */
export function summaries(s: GameSettings, f: SummaryFacts = {}): Record<SectionId, string> {
  const out = {} as Record<SectionId, string>;
  for (const id of SECTION_IDS) out[id] = summaryOf(id, s, f);
  return out;
}
